import React, { useState, useRef, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import CoveragesForQuote from 'components/Page 6/CoveragesForQuote';
import Footer from 'components/Additional/Footer/Footer';
import SfiLoader from 'components/Additional/SfiLoader/SfiLoader';
import useToast from 'components/Additional/Toast/useToast';
import Navigation from '../Overlays/Navigation';
import ErrorMessage from 'components/Additional/ErrorMessage/ErrorMessage';
import DidYouKnow from 'components/Additional/DidYouKnow/DidYouKnow';
import coveragesHelper from './CoveragesHelper';
import StopHere from './StopHere';
import {
	createActivityQuoteDB,
	completeQuote,
	createQuoteDB,
	getToken,
	getApplicationCorrespondence,
} from '../API/Model';
import { AppContext } from 'context/app-context/AppContext';
import { createQuoteByType } from 'tools/CreateQuoteHelper';
import {
	saveCreatedQuotesNumbers,
	getCreatedQuotesNumbers,
} from 'tools/LocalStorageHelper';
import NextButton from 'components/Additional/NextButton/NextButton';
import 'components/styles/styles.css';
import styles from './Coverages_page.module.css';

function Coverages_page() {
	let history = useHistory();

	const { navigationTitle, fetchQuoteAndLog, setDisplayedQuoteNumber } =
		useContext(AppContext);

	const { showToast } = useToast();

	const page = 4;
	const PAGE_NAME = 'Coverages';

	const contactEmail = useRef(localStorage.getItem('brand_email'));
	const [calculatedQuotes, setCalculatedQuotes] = useState([]);
	const [resultCalculations, setResultCalculations] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [stopHere, setStopHere] = useState(false);
	const [activeTab, setActiveTab] = useState('');
	const [fetchQuoteError, setFetchQuoteError] = useState(false);
	const [expandedTabs, setExpandedTabs] = useState([]);
	const [isMobile, setIsMobile] = useState(
		window.innerWidth <= 768 || window.innerHeight <= 500,
	);

	const tabNameMapper = {
		HO3: {
			tabName: 'Signature+',
			tooltip:
				'Our standard homeowner’s policy that provides replacement cost coverage on the dwelling/structure.',
		},
		HO4: { tabName: 'Renters', tooltip: '' },
		HO5: {
			tabName: 'Premier',
			tooltip:
				'Provides more comprehensive/additional coverage and is subject to stricter underwriting review.',
		},
		HO6: { tabName: 'Condo', tooltip: '' },
		'DF3-DL': {
			tabName: 'Landlord',
			tooltip:
				'Provides similar coverage as our standard homeowner’s policy with the ability to limit/exclude certain coverages to fit your needs. Only available for owners of rental properties.',
		},
		'DF3-DO': {
			tabName: 'Flex',
			tooltip:
				'Provides similar coverage as our standard homeowner’s policy with the ability to limit/exclude certain coverages to fit your needs. Available for individuals and entity-owned properties.',
		},
		DF1: {
			tabName: 'Basic',
			tooltip:
				'Our most basic policy in terms of coverage. Coverage is provided on an actual cash value/limited basis with certain coverages excluded.',
		},
	};

	const getTemplateId = (quoteType) => {
		switch (quoteType) {
			case 'HO3':
				return 'HO3-Quote';
			case 'HO4':
				return 'HO4-Quote';
			case 'HO5':
				return 'HO5-Quote';
			case 'HO6':
				return 'HO6-Quote';
			case 'DF1':
				return 'DF1-Quote';
			case 'DF3-DO':
				return 'DF3-Quote';
			case 'DF3-DL':
				return 'DF3DL-Quote';
		}
	};

	const getPremiumAmount = (response) =>
		Number(response.DTOBasicPolicy[0].FinalPremiumAmt ?? 0);

	const getCorrespondence = async (quoteNumber, quoteType) => {
		createActivityQuoteDB(
			'Coverages',
			'API_REQ',
			'Get ApplicationCorrespondenceToken request',
		);

		const token = await getToken(JSON.stringify({}));
		const isTokenFetched = token !== 'ERROR';

		createActivityQuoteDB(
			'Coverages',
			'API_RES',
			`Get ApplicationCorrespondenceToken response - ${
				isTokenFetched ? 'SUCCESS' : 'ERROR'
			}`,
		);

		if (!isTokenFetched) return;

		createActivityQuoteDB(
			'Coverages',
			'API_REQ',
			'Get ApplicationCorrespondence request',
		);

		const docUrl = await getApplicationCorrespondence(
			JSON.stringify({
				ApplicationNumber: quoteNumber,
				CorrespondenceTemplateId: getTemplateId(quoteType),
				Token: token,
			}),
		);

		const isUrlFetched = docUrl !== 'ERROR' && docUrl !== 'Error';

		createActivityQuoteDB(
			'Coverages',
			'API_RES',
			`Get ApplicationCorrespondence response - ${
				isUrlFetched ? 'SUCCESS' : 'ERROR'
			}`,
		);

		return isUrlFetched ? docUrl : null;
	};

	const onSubmitClick = () => {
		let selectedQuoteNumber;
		if (isMobile) {
			selectedQuoteNumber = expandedTabs.length
				? calculatedQuotes.find((cq) =>
						expandedTabs.includes(cq.quoteType),
					).quoteNumber
				: calculatedQuotes[0].quoteNumber;
		} else {
			selectedQuoteNumber = calculatedQuotes.find(
				(cq) => cq.quoteType === activeTab,
			).quoteNumber;
		}

		history.push(`/YourInfo?selectedQuoteNumber=${selectedQuoteNumber}`);
	};

	const toggleTab = (qt) => {
		setExpandedTabs((prev) =>
			prev.includes(qt) ? prev.filter((id) => id !== qt) : [...prev, qt],
		);
	};

	const onStopHereClick = () => {
		setStopHere(true);
		window.setTimeout(function () {
			window.location.href = 'https://www.securityfirstflorida.com';
		}, 5000);
	};

	const calculateResult = async (fetchResponse, quoteType) => {
		const result = {};

		const quoteNumber = fetchResponse.DTOBasicPolicy[0].QuoteNumber;
		const dtoBuilding = fetchResponse.DTOLine[0].DTORisk[0].DTOBuilding[0];
		const premiumAmount = getPremiumAmount(fetchResponse);
		const premiumAmountAndFees =
			coveragesHelper.calculatePremiumAmountAndFees(
				premiumAmount,
				fetchResponse.DTOFee,
			);

		if (premiumAmountAndFees === 0) return false;

		const limits = coveragesHelper.calculateLimits(dtoBuilding, quoteType);

		const deductibles = coveragesHelper.calculateDeductibles(
			dtoBuilding,
			quoteType,
		);

		const additionalCoverages =
			coveragesHelper.calculateAdditionalCoverages(
				dtoBuilding,
				quoteType,
			);

		const docUrl = await getCorrespondence(quoteNumber, quoteType);

		result[quoteType] = {
			...result[quoteType],
			quoteNumber,
			premiumAmountAndFees,
			limits,
			deductibles,
			additionalCoverages,
			docUrl: docUrl,
		};
		setResultCalculations((prev) => ({
			...prev,
			...result,
		}));
		setCalculatedQuotes((prev) => {
			prev.push({
				quoteType,
				quoteNumber,
				premiumAmountAndFees,
				limits,
				deductibles,
				additionalCoverages,
				docUrl: docUrl,
			});
			const sorted = prev.sort(
				(a, b) => a.premiumAmountAndFees - b.premiumAmountAndFees,
			);
			setActiveTab(sorted[0].quoteType);
			setExpandedTabs([sorted[0].quoteType]);
			return sorted;
		});

		return true;
	};

	const createApplicableQuotes = async (quoteTypes) => {
		const createdQuotesNumbers = [];
		for (const qt of quoteTypes) {
			const response = await createQuoteByType(qt);

			if (!Array.isArray(response)) {
				showToast(
					'We are sorry but we are unable to provide your quote details.  Please contact our customer support at (111) 111-1111 for assistance.',
					'Error',
				);
				continue;
			}

			const dtoApplication = response[0].DTOApplication[0];
			const payloadJson = JSON.parse(
				localStorage.getItem('create-quote-db'),
			);
			payloadJson.Quote_Number = dtoApplication.ApplicationNumber;
			await createQuoteDB(payloadJson);
			const isCalculated = await calculateResult(dtoApplication, qt);
			if (!isCalculated) {
				continue;
			}
			createdQuotesNumbers.push(dtoApplication.ApplicationNumber);
			await completeQuote(dtoApplication.ApplicationNumber);
		}
		saveCreatedQuotesNumbers(createdQuotesNumbers);
	};

	useEffect(() => {
		const handleResize = () =>
			setIsMobile(window.innerWidth <= 768 || window.innerHeight <= 500);
		window.addEventListener('resize', handleResize);

		const quoteTypes = JSON.parse(
			localStorage.getItem('applicableQuoteTypes'),
		);

		createActivityQuoteDB('Coverages', 'PL', 'Coverages page loaded');
		localStorage.setItem('current_page', 'Coverages');
		setIsLoading(true);

		async function fetchQuoteAndCalculateResult(quoteNumber) {
			const response = await fetchQuoteAndLog(PAGE_NAME, quoteNumber);
			if (response === 'Error') return false;

			const quoteType = response.DTOBasicPolicy[0].SubTypeCd;
			await calculateResult(response, quoteType);

			return true;
		}

		const showError = () => {
			setFetchQuoteError(true);
			setIsLoading(false);
		};

		(async () => {
			if (!quoteTypes.length) {
				showError();
				return;
			}

			const initialQuoteNumber = localStorage.getItem('quote_number');
			const initialQuoteType = localStorage.getItem('policy_type');
			const status =
				await fetchQuoteAndCalculateResult(initialQuoteNumber);
			if (!status) {
				showError();
				return;
			}

			await completeQuote(initialQuoteNumber);

			if (quoteTypes.length > 1) {
				const createdQuoteNumbers = getCreatedQuotesNumbers();
				if (createdQuoteNumbers === null) {
					const quoteTypesToCreate = quoteTypes.filter(
						(qt) => qt !== initialQuoteType,
					);
					await createApplicableQuotes(quoteTypesToCreate);
				} else {
					for (const cqn of createdQuoteNumbers) {
						await fetchQuoteAndCalculateResult(cqn);
					}
				}
			}

			setIsLoading(false);
		})();

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (fetchQuoteError) return;

		if (
			!isLoading &&
			(!Object.values(resultCalculations).length ||
				(Object.values(resultCalculations).length &&
					Object.values(resultCalculations).every(
						(v) => v.premiumAmountAndFees === 0,
					)))
		) {
			localStorage.setItem('updatedUserDataPdfUrl', null);
			history.push('/YourInfo');
		}

		if (
			!isLoading &&
			(!calculatedQuotes.length ||
				(calculatedQuotes.length &&
					calculatedQuotes.every(
						(cq) => cq.premiumAmountAndFees === 0,
					)))
		) {
			localStorage.setItem('updatedUserDataPdfUrl', null);
			history.push('/YourInfo');
		}
	}, [isLoading, fetchQuoteError, resultCalculations, calculatedQuotes]);

	const errorMessageComponent = (
		<div
			style={{
				height: '60vh',
				display: 'flex',
				alignItems: 'center',
			}}
		>
			<ErrorMessage
				contactEmail={contactEmail.current}
				phoneNumber={window.APP_CONFIG.CONTACT_PHONE}
			/>
		</div>
	);

	if (fetchQuoteError) return errorMessageComponent;

	return (
		<div className="base-wrapper">
			<div className="abs-wrapper">
				<Navigation page={page} progress={4} />
			</div>
			{stopHere ? (
				<StopHere />
			) : (
				<>
					{isLoading ? (
						<DidYouKnow title="Calculating your estimate" />
					) : (
						<div className={styles.coverages_content_wrapper}>
							<div className={styles.wrapper} id="content">
								{calculatedQuotes.length ? (
									<div>
										{isMobile ? (
											// 📱 Mobile view
											<div
												className={
													styles.coverages_mobile_tabs
												}
											>
												{navigationTitle && (
													<div className="flex w-full my-2 justify-center">
														<span className="text-lg font-semibold text-sfi-blue">
															{navigationTitle}
														</span>
													</div>
												)}
												<div
													className={
														styles.bottom_buttons
													}
												>
													<NextButton
														text="I'm interested, let's keep going"
														variant="main"
														onClick={onSubmitClick}
													/>
												</div>
												{calculatedQuotes.map(
													(cq, index) => {
														const {
															quoteType,
															premiumAmountAndFees,
														} = cq;
														const tabNameMapperValue =
															tabNameMapper[
																quoteType
															];

														const monthlyPremium =
															coveragesHelper.formatNumber(
																coveragesHelper.calculateMonthlyPremium(
																	premiumAmountAndFees,
																),
															);
														const isExpanded =
															expandedTabs.includes(
																quoteType,
															);

														return (
															<div
																key={index}
																className={
																	styles.mobile_tab_item
																}
															>
																<button
																	className={
																		styles.mobile_tab_header
																	}
																	onClick={() =>
																		toggleTab(
																			quoteType,
																		)
																	}
																>
																	<span>
																		{
																			tabNameMapperValue.tabName
																		}
																	</span>
																	<div
																		className={
																			styles.mobile_tab_right
																		}
																	>
																		<span>{`${monthlyPremium}/mo`}</span>
																		<svg
																			className={`${
																				styles.arrow_icon
																			} ${
																				isExpanded
																					? styles.rotate
																					: ''
																			}`}
																			width="18"
																			height="18"
																			viewBox="0 0 24 24"
																			fill="none"
																			stroke="var(--main-blue-color)"
																			strokeWidth="3"
																			strokeLinecap="round"
																			strokeLinejoin="round"
																		>
																			<polyline points="6 9 12 15 18 9" />
																		</svg>
																	</div>
																</button>
																{isExpanded && (
																	<div
																		className={
																			styles.mobile_tab_content
																		}
																	>
																		<CoveragesForQuote
																			calculations={
																				cq
																			}
																			onSubmit={
																				onSubmitClick
																			}
																			onStop={
																				onStopHereClick
																			}
																		/>
																	</div>
																)}
															</div>
														);
													},
												)}
												<div
													className={
														styles.bottom_buttons
													}
												>
													<NextButton
														text="Thanks, I'll Stop here"
														variant="transparent"
														onClick={
															onStopHereClick
														}
													/>
												</div>
											</div>
										) : (
											// 💻 Desktop view
											<>
												<div>
													<div
														className={
															styles.coverages_tab_bar
														}
													>
														<div
															className={
																styles.tabs_container
															}
														>
															{calculatedQuotes.map(
																(cq, index) => {
																	const {
																		quoteType,
																		quoteNumber,
																	} = cq;
																	const isActive =
																		activeTab ===
																		quoteType;
																	const tabNameMapperValue =
																		tabNameMapper[
																			quoteType
																		];
																	return (
																		<button
																			key={
																				index
																			}
																			className={`${
																				styles.coverages_tab_button
																			} ${
																				styles.tooltip_bottom
																			} ${
																				isActive
																					? styles.active
																					: ''
																			}`}
																			onClick={() => {
																				setActiveTab(
																					quoteType,
																				);
																				setDisplayedQuoteNumber(
																					quoteNumber,
																				);
																			}}
																			data-tooltip={
																				tabNameMapperValue.tooltip
																			}
																		>
																			{
																				tabNameMapperValue.tabName
																			}
																		</button>
																	);
																},
															)}
														</div>
														<div
															className={
																styles.continue_button_container
															}
														>
															<NextButton
																text="I'm interested, let's keep going"
																variant="main"
																onClick={
																	onSubmitClick
																}
															/>
														</div>
													</div>
												</div>
												<div
													className={
														styles.tab_content_container
													}
												>
													{!!resultCalculations[
														activeTab
													] ? (
														<>
															<CoveragesForQuote
																calculations={
																	resultCalculations[
																		activeTab
																	]
																}
																onSubmit={
																	onSubmitClick
																}
																onStop={
																	onStopHereClick
																}
															/>
															<div
																className={
																	styles.bottom_buttons
																}
															>
																<NextButton
																	text="Thanks, I'll Stop here"
																	variant="transparent"
																	onClick={
																		onStopHereClick
																	}
																/>
															</div>
														</>
													) : (
														<SfiLoader />
													)}
												</div>
											</>
										)}
									</div>
								) : (
									errorMessageComponent
								)}
								<Footer />
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}

export default Coverages_page;
